import React, { useEffect, useState } from 'react';
import { useAppStore } from '../store';
import TrendingProjectCard from '../components/trending/TrendingProjectCard';
import TrendingPosterView from '../components/trending/TrendingPosterView';
import TrendingDateSelector from '../components/trending/TrendingDateSelector';
import TrendingAdminPanel from '../components/trending/TrendingAdminPanel';
import { TrendingProject } from '../types';
import '../styles/TrendingPage.css';

const TrendingPage: React.FC = () => {
  const {
    user,
    isAuthenticated,
    trendingProjects,
    trendingLoading,
    trendingDates,
    selectedTrendingDate,
    error,
    loadTrendingProjects,
    loadTrendingDates,
    setSelectedTrendingDate,
    loadFavorites,
    clearError
  } = useAppStore();

  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [displayMode, setDisplayMode] = useState<'card' | 'poster'>('card');

  useEffect(() => {
    // 页面加载时获取数据
    loadTrendingDates();
    loadTrendingProjects();
  }, [loadTrendingDates, loadTrendingProjects]);

  useEffect(() => {
    // 用户登录时加载收藏列表
    if (isAuthenticated) {
      loadFavorites();
    }
  }, [isAuthenticated, loadFavorites]);

  useEffect(() => {
    // 清除错误信息
    if (error) {
      const timer = setTimeout(() => {
        clearError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleDateChange = (date: string) => {
    setSelectedTrendingDate(date);
  };

  const handleDataUpdate = () => {
    // 刷新项目数据
    loadTrendingProjects();
  };

  const isAdmin = user?.is_admin || false;

  return (
    <div className="trending-page">
      {/* 页面头部 */}
      <div className="trending-header">
        <div className="trending-title">
          <i className="fab fa-github"></i>
          <h1>GitHub Trending</h1>
          <span className="trending-subtitle">发现今日最热门的开源项目</span>
        </div>
        
        {isAdmin && (
          <div className="trending-admin-controls">
            <button
              className="btn btn-admin"
              onClick={() => setShowAdminPanel(!showAdminPanel)}
              title="管理员控制面板"
            >
              <i className="fas fa-cog"></i>
              管理
            </button>
          </div>
        )}
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {/* 管理员面板 */}
      {isAdmin && showAdminPanel && (
        <TrendingAdminPanel
          onClose={() => setShowAdminPanel(false)}
          onDataUpdate={handleDataUpdate}
        />
      )}

      {/* 项目列表 */}
      <div className="trending-content">
        {trendingLoading ? (
          <div className="trending-loading">
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
            <p>正在加载 Trending 项目...</p>
          </div>
        ) : trendingProjects.length === 0 ? (
          <div className="trending-empty">
            <div className="empty-icon">
              <i className="fab fa-github"></i>
            </div>
            <h3>暂无数据</h3>
            <p>
              {selectedTrendingDate === new Date().toISOString().split('T')[0]
                ? '今日的 Trending 数据尚未获取'
                : `${selectedTrendingDate} 的数据不可用`}
            </p>
            {isAdmin && (
              <button
                className="btn btn-primary"
                onClick={() => setShowAdminPanel(true)}
              >
                <i className="fas fa-download"></i>
                获取数据
              </button>
            )}
          </div>
        ) : (
          <div className="trending-projects">
            <div className="trending-stats">
              <span className="stats-item">
                <i className="fas fa-calendar"></i>
                {selectedTrendingDate}
              </span>
              <span className="stats-item">
                <i className="fas fa-star"></i>
                {trendingProjects.length} 个项目
              </span>
              <span className="stats-item">
                <i className="fas fa-brain"></i>
                {trendingProjects.filter(p => p.ai_interpretation).length} 个AI解读
              </span>
            </div>

            {/* 展示模式切换按钮 */}
            <div className="display-mode-selector">
              <button
                className={`mode-btn ${displayMode === 'card' ? 'active' : ''}`}
                onClick={() => setDisplayMode('card')}
              >
                <i className="fas fa-th-large"></i>
                卡片展示
              </button>
              <button
                className={`mode-btn ${displayMode === 'poster' ? 'active' : ''}`}
                onClick={() => setDisplayMode('poster')}
              >
                <i className="fas fa-image"></i>
                海报展示
              </button>
            </div>

            {/* 根据展示模式显示不同的组件 */}
            {displayMode === 'card' ? (
              <div className="trending-projects-grid">
                {trendingProjects.map((project: TrendingProject) => (
                  <TrendingProjectCard
                    key={project.id}
                    project={project}
                  />
                ))}
              </div>
            ) : (
              <TrendingPosterView
                projects={trendingProjects}
                selectedDate={selectedTrendingDate}
              />
            )}
          </div>
        )}
      </div>

      {/* 日期选择器 */}
      <TrendingDateSelector
        dates={trendingDates}
        selectedDate={selectedTrendingDate}
        onDateChange={handleDateChange}
        loading={trendingLoading}
      />

      {/* 页面底部信息 */}
      <div className="trending-footer">
        <p>
          <i className="fas fa-info-circle"></i>
          数据来源于 GitHub Trending，每日更新
        </p>
        {isAuthenticated && !isAdmin && (
          <p className="admin-note">
            <i className="fas fa-lock"></i>
            仅管理员可更新数据和生成AI解读
          </p>
        )}
      </div>
    </div>
  );
};

export default TrendingPage;
